import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Log } from '@/utils/logger';

interface Notification {
  id: string;
  title: string;
  message: string;
  created_at: string;
  type: 'feedback' | 'payment';
  redirect_url?: string;
  read_at: string | null;
}

export const useOptimizedNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const channelRef = useRef<any>(null);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();

  // Debounced fetch to reduce database calls
  const debouncedFetch = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    fetchTimeoutRef.current = setTimeout(fetchNotifications, 300);
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setNotifications([]);
        setUnreadCount(0);
        setIsLoading(false);
        return;
      }

      // Fetch notifications with read_at column
      const { data: notifications, error } = await supabase
        .from('notifications')
        .select('id, title, message, type, redirect_url, created_at, read_at')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        Log.error('Error fetching notifications', { error: error.message });
        setIsLoading(false);
        return;
      }

      if (!notifications) {
        setNotifications([]);
        setUnreadCount(0);
        setIsLoading(false);
        return;
      }

      // Transform notifications
      const transformedNotifications: Notification[] = notifications.map(notification => ({
        id: notification.id,
        title: notification.title,
        message: notification.message,
        type: notification.type as 'feedback' | 'payment',
        redirect_url: notification.redirect_url,
        created_at: notification.created_at,
        read_at: notification.read_at
      }));

      setNotifications(transformedNotifications);
      
      // Count unread notifications (those without read_at timestamp)
      const unreadNotifications = transformedNotifications.filter(n => !n.read_at);
      setUnreadCount(unreadNotifications.length);
      setIsLoading(false);
    } catch (error: any) {
      Log.error('Error fetching notifications', { error: error.message });
      setIsLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const readAt = new Date().toISOString();

      // Optimistic update first
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read_at: readAt } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Update database
      await supabase
        .from('notifications')
        .update({ read_at: readAt })
        .eq('id', notificationId);

    } catch (error: any) {
      Log.error('Error marking notification as read', { error: error.message });
      // Revert optimistic update on error
      setNotifications(prev =>
        prev.map(n => 
          n.id === notificationId ? { ...n, read_at: null } : n
        )
      );
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const unreadNotifications = notifications.filter(n => !n.read_at);
      if (unreadNotifications.length === 0) return;

      const readAt = new Date().toISOString();

      // Optimistic update
      setNotifications(prev => prev.map(n => ({ ...n, read_at: readAt })));
      setUnreadCount(0);

      // Update all unread notifications
      const notificationIds = unreadNotifications.map(n => n.id);
      await supabase
        .from('notifications')
        .update({ read_at: readAt })
        .in('id', notificationIds);

    } catch (error: any) {
      Log.error('Error marking all notifications as read', { error: error.message });
      // Revert optimistic update on error
      debouncedFetch();
    }
  }, [notifications, debouncedFetch]);

  useEffect(() => {
    // Initial fetch
    fetchNotifications();

    // Set up realtime subscription with proper cleanup
    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Clean up existing channel
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }

      // Create new channel with optimized configuration
      channelRef.current = supabase
        .channel('notifications-optimized')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications'
          },
          (payload) => {
            const newNotification = payload.new as any;
            setNotifications(prev => {
              // Avoid duplicates
              if (prev.some(n => n.id === newNotification.id)) {
                return prev;
              }
              
              const notification: Notification = {
                id: newNotification.id,
                title: newNotification.title,
                message: newNotification.message,
                type: newNotification.type,
                redirect_url: newNotification.redirect_url,
                created_at: newNotification.created_at,
                read_at: newNotification.read_at
              };
              
              return [notification, ...prev.slice(0, 9)];
            });
            setUnreadCount(prev => prev + 1);
          }
        )
        .subscribe();
    };

    setupRealtimeSubscription();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refetch: debouncedFetch
  };
};